/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import {
    Select,
    useNumberInput,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'

/**
 * This is the mobile implementation of the Chakra NumberInput. This simple component essentially
 * is a helper so we don't have to reuse the hooks every time we need a number input since design dictates
 * we use the moobile variation on all screens.
 *
 * NOTE: We can optionally put global logic we see if in here, and various styling decisions in this single
 * component.
 *
 * @param {*} props
 * @returns
 */
const QuantityPicker = (props) => {
    const intl = useIntl()
    const formStyles = useStyleConfig('FormStyles')
    const {getInputProps} = useNumberInput({
        ...props,
        // Defaults
        focusInputOnChange: false,
        onFocus: (e) => {
            const {onFocus} = props

            // This is useful for mobile devices, this allows the user to pop open the keyboard and set the
            // new quantity with one click.
            e.target.select()

            // If there is a `onFocus` property define, call it with the event captured.

            onFocus?.call(this, e)
        }
    })

    const input = getInputProps({
        width: {base: '56px', xs: '80px'},
        height: {base: '40px', xs: '56px'},
        borderColor: 'gray.600',
        borderRadius: '3px',
        textAlign: 'center',
        fontWeight: '500',
        fontSize: {base: '14px', xs: '16px'},
        color: 'black.300',
        _hover: {
            borderColor: 'gray.600'
        },
        isDisabled: props?.isDisabled || false,
        'aria-label': intl.formatMessage({
            defaultMessage: 'Quantity',
            id: 'product_view.label.quantity'
        })
    })

    return (
        <Select {...input} {...formStyles.selectField}>
            {[...Array(props?.max).keys()].map((i) => (
                <option key={i} value={i + 1}>
                    {i + 1}
                </option>
            ))}
        </Select>
    )
}

QuantityPicker.propTypes = {
    productName: PropTypes.string,
    max: PropTypes.number.isRequired,
    value: PropTypes.number,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    isDisabled: PropTypes.bool
}

export default QuantityPicker
